import { Injectable } from "@angular/core";
import { AlphanumericCounter } from "./websocket/idGenerator.service";
import { addMilliseconds, getTime } from "date-fns";
import { BehaviorSubject, Observable, filter, map } from "rxjs";

/**
 * GLOBAL TIMER SERVICE - ANGULAR
 * 
 * Objective: Centralize and optimize management of multiple simultaneous timers
 * 
 * Core Logic:
 * 1. Unification - Replaces multiple setTimeouts with a single central manager
 * 2. Efficiency - Handles absurd amount of timers with minimal performance impact
 * 3. Smart Grouping - Groups timers by expiration date for batch execution
 * 
 * Basic Flow:
 * 1. Create Timer → Generates unique ID and registers in 3 structures:
 *    a) timersInfoList: Complete timer metadata
 *    b) timePairsList: Grouping by expiration date
 *    c) ascendentEndDate...: Priority-ordered execution queue
 * 
 * 2. Scheduling - Maintains only 1 active setTimeout (next to expire)
 * 
 * 3. Expiration:
 *    a) Executes all expired timers in batch
 *    b) Notifies subscribers
 *    c) Cleans references
 *    d) Schedules next nearest timer
 * 
 * Key Mechanisms:
 * - Binary Search: For efficient ordered insertion (O(log n))
 * - Batch Processing: Mass execution of same-date timers
 * - Garbage Collection: Triple cleanup (3 synchronized structures)
 * - Grouped Notifications: Emits events in batches for better performance
 * 
 * Important Considerations:
 * - Timers with same expiration execute together without specific order
 * - Any timer can be implicitly canceled by reference removal
 * - System always prioritizes temporally nearest timer
 * - Automatic self-healing on execution errors
 */
@Injectable({
  providedIn: "root",
})
export class GlobalTimerService {
  // Timer management structures
  private alphanumericCounter = new AlphanumericCounter();
  private timersInfoList: TimersInfoList = {};         // Map of all active timers by ID
  private timePairsList: TimePairList = {};            // Timers grouped by expiration date
  private ascendentEndDateTimersIdAscendentOrdered: string[] = []; // Timers ordered by expiration
  private currentTimer?: ReturnType<typeof setTimeout>;                          // Reference to active timeout
  private timersFinished = new BehaviorSubject<ReadonlySet<string> | null>(null);   // Notification stream
  constructor() { }

  //#region Public API
  /**
   * Get observable for all timer completions
   */
  getAllTimersObservable(): Observable<ReadonlySet<string>> {
    return this.timersFinished.pipe(filter((t): t is Set<string> => !!t));
  }

  /**
   * Get observable for specific timer completion
   * @param timerId ID of the timer to monitor
   */
  getTimerObservableById(timerId: string): Observable<boolean> {
    return this.timersFinished.pipe(
      filter((t): t is Set<string> => !!t && t.has(timerId)),
      map(() => true)
    );
  }

  /**
   * Get observable for a group of timers
   * @param timers Array of timer IDs to monitor
   */
  etGroupOfTimersObservable(timers: string[]): Observable<string[]> {
    return this.timersFinished.pipe(
      filter((t): t is Set<string> => !!t),
      map(set => timers.filter(id => set.has(id))),
      filter(expiredInGroup => expiredInGroup.length > 0)
    );
  }

  /**
   * Create timer ending at specific ISO date
   * @param date ISO string of expiration time
   */
  createTimerEndingAt(date: string): string {
    return this.createNewTimer(date);
  }

  /**
   * Create timer with duration from current time
   * @param timeInMs Duration in milliseconds
   */
  createTimerWithDuration(timeInMs: number): string {
    const date = addMilliseconds(new Date(), timeInMs).toISOString();
    return this.createNewTimer(date);
  }
  //#endregion

  //#region Core Timer Logic
  /**
   * Creates new timer and maintains sorted order
   * @param date ISO expiration date
   */
  private createNewTimer(date: string): string {
    const timerId = this.alphanumericCounter.getNextId(true);
    this.timersInfoList[timerId] = { timerId, endDate: date };

    // Initialize date group if needed
    if (!this.timePairsList[date]) {
      this.timePairsList[date] = [];
    }
    this.timePairsList[date].push(timerId);

    // Handle first timer case
    if (this.ascendentEndDateTimersIdAscendentOrdered.length === 0) {
      this.ascendentEndDateTimersIdAscendentOrdered.push(timerId);
      this.startNextTimer();
      return timerId;
    }

    // Insert in sorted position
    const insertIndex = this.findInsertionIndex(date);
    this.ascendentEndDateTimersIdAscendentOrdered.splice(insertIndex, 0, timerId);

    // Restart timer if new earliest expiration
    if (insertIndex === 0) {
      this.clearCurrentTimer();
      this.startNextTimer();
    }

    return timerId;
  }

  /**
   * Finds insertion index using binary search for efficient sorting
   * @param date ISO date to find position for
   */
  private findInsertionIndex(date: string): number {
    const targetTime = getTime(new Date(date));
    let low = 0;
    let high = this.ascendentEndDateTimersIdAscendentOrdered.length;

    // Binary search for insertion point
    while (low < high) {
      const mid = (low + high) >>> 1;  // Bitwise division by 2
      const midTime = getTime(new Date(
        this.timersInfoList[this.ascendentEndDateTimersIdAscendentOrdered[mid]].endDate
      ));

      midTime <= targetTime ? (low = mid + 1) : (high = mid);
    }

    // Adjust for timers with same expiration time
    while (low > 0 && getTime(new Date(
      this.timersInfoList[this.ascendentEndDateTimersIdAscendentOrdered[low - 1]].endDate
    )) === targetTime) {
      low--;
    }

    return low;
  }

  /**
   * Manages timer lifecycle and expiration handling
   */
  private startNextTimer(): void {
    this.clearCurrentTimer();
    const currentTimeMs = getTime(new Date());
    const expiredBatch = new Set<string>();

    while (this.ascendentEndDateTimersIdAscendentOrdered.length > 0) {
      const nextId = this.ascendentEndDateTimersIdAscendentOrdered[0];
      const endTime = getTime(new Date(this.timersInfoList[nextId].endDate));

      if (endTime > currentTimeMs) break;

      expiredBatch.add(nextId);
      this.ascendentEndDateTimersIdAscendentOrdered.shift();
    }

    if (expiredBatch.size > 0) {
      this.emitAndClean(expiredBatch);
    }

    if (this.ascendentEndDateTimersIdAscendentOrdered.length === 0) return;

    const nextTimerId = this.ascendentEndDateTimersIdAscendentOrdered[0];
    const nextEndTime = getTime(new Date(this.timersInfoList[nextTimerId].endDate));
    const ms = nextEndTime - Date.now();

    if (ms <= 0) {
      this.startNextTimer();
      return;
    }

    this.currentTimer = setTimeout(() => {
      this.handleTimerExpiration(nextTimerId);
    }, ms);
  }

  /**
   * Handles final expiration logic for timers
   * @param timerId ID of expiring timer
   */
  private handleTimerExpiration(timerId: string): void {
    if (!this.timersInfoList[timerId]) return;

    const date = this.timersInfoList[timerId].endDate;
    const idsAtSameTime = this.timePairsList[date] || [];

    this.emitAndClean(new Set(idsAtSameTime));
    this.startNextTimer();
  }

  /**
     * Centraliza la notificación y limpieza
     */
  private emitAndClean(batch: Set<string>): void {
    this.timersFinished.next(batch);
    this.cleanTimers(batch);
  }

  /**
   * Atomic cleanup of timer references
   * @param timersId Array of timer IDs to clean
   */
  private cleanTimers(timersId: Set<string> | string[]): void {
    timersId.forEach(id => {
      const info = this.timersInfoList[id];
      if (!info) return;

      const date = info.endDate;

      if (this.timePairsList[date]) {
        const idx = this.timePairsList[date].indexOf(id);
        if (idx > -1) {
          this.timePairsList[date].splice(idx, 1);
          if (this.timePairsList[date].length === 0) delete this.timePairsList[date];
        }
      }

      const orderIdx = this.ascendentEndDateTimersIdAscendentOrdered.indexOf(id);
      if (orderIdx > -1) this.ascendentEndDateTimersIdAscendentOrdered.splice(orderIdx, 1);

      delete this.timersInfoList[id];
    });
  }

  /**
   * Clears current timeout reference
   */
  private clearCurrentTimer(): void {
    if (this.currentTimer) {
      clearTimeout(this.currentTimer);
      this.currentTimer = undefined;
    }
  }
  //#endregion
}

//#region Type Definitions
/**
 * Timer metadata structure
 */
interface TimerData {
  timerId: string;
  endDate: string;
}

/**
 * Map of timer IDs to their metadata
 */
interface TimersInfoList {
  [timerId: string]: TimerData;
}

/**
 * Grouping of timer IDs by expiration date
 */
interface TimePairList {
  [date: string]: string[];
}
//#endregion